/**
 * The shell, the navigation and the addresses.
 *
 * One `AppShell` for the whole application rather than one per screen: the
 * shell holds who is signed in, the way out, and the navigation, and a second
 * copy of any of those is a second thing to keep in step.
 */
import { useState } from "react";
import { AppShell, Button, EmptyState, PageHeader } from "@depicomnlbv/platform-ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { createQueryClient, fixtureMode } from "./api";
import { APP_NAME } from "./appName";
import { session, signedInAs } from "./auth/session";
import { Failure, Pending } from "./components/Failure";
import { Nav } from "./components/Nav";
import { Closures } from "./pages/Closures";
import { BlockDaysPage } from "./pages/BlockDays";
import { Day } from "./pages/Day";
import { MilkFlowDayPage, MilkFlowFarmPage, MilkFlowPage } from "./pages/MilkFlow";
import { OperatingPlanPage } from "./pages/OperatingPlan";
import { Runs } from "./pages/Runs";
import { SalesPage, ToDoPage } from "./pages/Sales";
import { TradingPage } from "./pages/Trading";
import { Week } from "./pages/Week";
import { useTitle } from "./useTitle";
import { FEEDBACK_API_BASE, feedbackToken, screenPattern } from "./feedback";
import { FeedbackLauncher } from "./vendor/feedback/Launcher";

export { APP_NAME };

/** The screen a configured deployment shows until somebody signs in. */
function SignIn() {
  const [asking, setAsking] = useState(false);
  useTitle("Sign in");

  return (
    <AppShell appName={APP_NAME}>
      <div className="screen">
        <PageHeader
          actions={
            <Button
              isDisabled={asking}
              onPress={() => {
                setAsking(true);
                void session.signIn();
              }}
            >
              {asking ? "Signing you in" : "Sign in"}
            </Button>
          }
          qualifier="The planner shows the milk collection plan to dispatchers. Sign in with your work account to read it."
          title="Sign in"
        />
      </div>
    </AppShell>
  );
}

function NothingHere() {
  useTitle("Nothing here");
  return (
    <>
      <PageHeader title="Nothing here" />
      <EmptyState>No screen at this address. Plan, in the navigation, shows the collections.</EmptyState>
    </>
  );
}

/** The shell and the screens, under whatever query client the caller gives.
 *  Exported for `reflow.pages.test.tsx`, which renders them to static pages. */
export function Screens() {
  // Read on render and not held in state: it changes only on a full page load,
  // because every way in and out of a session here is a redirect.
  const identity = signedInAs();
  const [signingOut, setSigningOut] = useState(false);

  return (
    <AppShell
      appName={APP_NAME}
      nav={<Nav />}
      // Fixture mode can only be on in a development build (`api/client.ts`),
      // and when it is, every screen says so: made-up trips must never be
      // mistaken for a plan.
      notice={
        fixtureMode
          ? {
              message:
                "Fixture data: these screens are answered from the API's test fixtures, not from a plan in the database.",
              tone: "neutral",
            }
          : undefined
      }
      // Absent where there is no session to end, which is the local case: a
      // sign-out that cannot sign anybody out is worse than no button.
      onSignOut={
        session.configured
          ? () => {
              setSigningOut(true);
              void session.signOut();
            }
          : undefined
      }
      signingOut={signingOut}
      user={identity ? { name: identity } : null}
    >
      {/* Every screen is the blocks in one column, spaced as the package's
          README asks ("Building a screen"). */}
      <div className="screen">
        <Routes>
          {/* Plan is the collections as they are driven now (16 October 2026); the optimiser's runs stay
              at /runs, out of the navigation, until the optimisation is reworked. */}
          <Route element={<Navigate replace to="/plan" />} path="/" />
          <Route element={<OperatingPlanPage />} path="/plan" />
          <Route element={<Runs />} path="/runs" />
          <Route element={<Week />} path="/runs/:runId/weeks/:monday" />
          <Route element={<Day />} path="/runs/:runId/days/:date" />
          <Route element={<Closures />} path="/closures" />
          {/* Milk sales and To do (16 October 2026, MV-3): no screen named after one buyer. */}
          <Route element={<SalesPage />} path="/sales" />
          <Route element={<ToDoPage />} path="/to-do" />
          <Route element={<TradingPage />} path="/trading" />
          <Route element={<MilkFlowPage />} path="/milk-flow" />
          <Route element={<MilkFlowDayPage />} path="/milk-flow/days/:date" />
          <Route element={<BlockDaysPage />} path="/milk-flow/block-days" />
          <Route element={<Navigate replace to="/milk-flow" />} path="/milk-flow/cheese-plan" />
          <Route element={<MilkFlowFarmPage />} path="/milk-flow/farms/:producer" />
          <Route element={<NothingHere />} path="*" />
        </Routes>
      </div>
    </AppShell>
  );
}

/**
 * The shared feedback panel, once for the whole application, so every screen
 * has it. Reports go to the one triage queue across all applications.
 *
 * Beside the shell and not inside it: it floats (position: fixed), and inside
 * it would be the last thing in <main>, after the explanation every screen
 * ends on (grammar.test.tsx).
 */
function Feedback() {
  const { pathname } = useLocation();
  return (
    <FeedbackLauncher
      apiBase={FEEDBACK_API_BASE}
      build={__BUILD_SHA__}
      currentScreen={screenPattern(pathname)}
      getToken={feedbackToken}
    />
  );
}

/**
 * The application, and the one cache under it.
 *
 * The provider is here and not in `main.tsx` beside the router because
 * `main.tsx` is the only file no test renders, so a provider that lived there
 * alone would be a provider nothing proves is mounted. The client is made once
 * per mount (`useState` with the factory), so a second render in one process,
 * which is what a test does, gets its own cache.
 */
export function App() {
  const identity = signedInAs();
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      {session.configured && !identity ? (
        <SignIn />
      ) : (
        <>
          <Screens />
          <Feedback />
        </>
      )}
    </QueryClientProvider>
  );
}
